<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import BaseFeedColumn from '../base-feed-column.vue'
import VideoItem from '../item.vue'
import type { FeedPreset, IRefVideoItem } from '../..'

const props = defineProps<{
  items: IRefVideoItem[]
  windowSize?: number
  isClearMode?: boolean
  playbackEnabled?: boolean
  navigationEnabled?: boolean
  preset?: FeedPreset
  showComments?: boolean
  showEpisodes?: boolean
}>()
const emit = defineEmits<{
  (e: 'reachEnd'): void
  (e: 'indexChange', index: number): void
  (e: 'like', index: number): void
  (e: 'collect', index: number): void
  (e: 'share', index: number): void
  (e: 'select-episode', episodeId: number): void
  (e: 'primary-action', sourceId: number): void
}>()
const items = toRef(props, 'items')
type FeedColumnExpose = {
  jumpToIndex: (index: number) => void
  currentIndex: number
  resetNavigationState: () => void
}

const feedRef = ref<FeedColumnExpose | null>(null)

defineExpose({
  jumpToIndex: (index: number) => feedRef.value?.jumpToIndex(index),
  currentIndex: computed(() => feedRef.value?.currentIndex ?? 0),
  resetNavigationState: () => feedRef.value?.resetNavigationState(),
})
</script>

<template>
  <BaseFeedColumn
    ref="feedRef"
    :item-count="items.length"
    :window-size="windowSize"
    :navigation-enabled="navigationEnabled"
    @reach-end="emit('reachEnd')"
    @index-change="emit('indexChange', $event)"
  >
    <template #default="{ renderedIndices, currentIndex }">
      <VideoItem
        v-for="idx in renderedIndices"
        :key="idx"
        :idx="idx"
        :items="items"
        :item="items[idx]!"
        :is-active="idx === currentIndex"
        :playback-enabled="playbackEnabled"
        :preset="preset"
        :hidden-actions="isClearMode"
        :show-comments="props.showComments"
        :show-episodes="props.showEpisodes"
        @select-episode="emit('select-episode', $event)"
        @primary-action="emit('primary-action', $event)"
      >
        <template #footer>
          <slot name="footer" :index="idx" :videoInfo="items[idx]" />
        </template>
      </VideoItem>
    </template>
  </BaseFeedColumn>
</template>
